/**
 * WRI Aqueduct Water Risk Service
 * Supabase'deki yerel WRI verilerinden çeker
 */

import { supabase } from '@/lib/supabaseClient';

interface WRIWaterRisk {
  basin_name: string;
  country: string;
  region: string;
  
  // Baseline (2019)
  bws_raw_baseline: number;  // Baseline Water Stress (0-5)
  bwd_raw_baseline: number;  // Baseline Water Depletion
  iav_raw_baseline: number;  // Interannual Variability
  sev_raw_baseline: number;  // Seasonal Variability
  gtd_raw_baseline: number;  // Groundwater Table Decline
  
  // Future projections
  bws_raw_2030: number;
  bwd_raw_2030: number;
  bws_raw_2050: number;
  bwd_raw_2050: number;
  
  // Risks
  rfr_raw: number;  // Riverine Flood Risk
  cfr_raw: number;  // Coastal Flood Risk
  drr_raw: number;  // Drought Risk
  
  // Quality
  ucw_raw: number;  // Unimproved Drinking Water
  cep_raw: number;  // Coastal Eutrophication Potential
}

/**
 * Koordinata göre en yakın WRI verisini bul
 */
export async function fetchWRIDataByLocation(lat: number, lon: number): Promise<WRIWaterRisk | null> {
  try {
    console.log(`🌊 WRI Aqueduct verisi çekiliyor: lat=${lat}, lon=${lon}`);

    // PostGIS ST_Distance kullanarak en yakın noktayı bul
    const { data, error } = await supabase.rpc('find_nearest_wri_basin', {
      p_lat: lat,
      p_lon: lon,
      p_limit: 1
    });

    if (error) {
      console.error('❌ WRI sorgu hatası:', error);
      return null;
    }

    if (!data || data.length === 0) {
      console.warn('⚠️ Konum için WRI verisi bulunamadı');
      return null;
    }

    console.log('✅ WRI Aqueduct verisi bulundu:', data[0].basin_name);
    return data[0];
  } catch (error) {
    console.error('❌ WRI fetch hatası:', error);
    return null;
  }
}

/**
 * Ülkeye göre WRI verilerini getir
 */
export async function fetchWRIDataByCountry(country: string): Promise<WRIWaterRisk[]> {
  try {
    const { data, error } = await supabase
      .from('wri_aqueduct_water_risk')
      .select('*')
      .eq('country', country)
      .limit(100);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ WRI ülke sorgusu hatası:', error);
    return [];
  }
}

/**
 * WRI skorlarını kategorilere dönüştür
 */
function scoreToCategory(score: number): string {
  if (score < 1) return 'Düşük';
  if (score < 2) return 'Düşük-Orta';
  if (score < 3) return 'Orta-Yüksek';
  if (score < 4) return 'Yüksek';
  return 'Aşırı Yüksek';
}

/**
 * WRI verisini su metriklerine dönüştür
 */
export function transformWRIToSuMetrikleri(wri: WRIWaterRisk) {
  return {
    su_stresi_2030: scoreToCategory(wri.bws_raw_2030),
    su_stresi_2050: scoreToCategory(wri.bws_raw_2050),
    su_talebi_artisi_2030: Math.round(wri.bws_raw_2030 * 15), // 0-75
    su_talebi_artisi_2050: Math.round(wri.bws_raw_2050 * 20), // 0-100
    endustriyel_su_kullanimi_degisimi: Math.round((wri.bwd_raw_2050 - wri.bwd_raw_baseline) * 20),
    yagis_degiskenlik_artisi: Math.round(wri.iav_raw_baseline * 15), // 0-75
    kuraklik_riski: scoreToCategory(wri.drr_raw),
    sel_riski: scoreToCategory(wri.rfr_raw),
    su_kalitesi_bozulma: Math.round(wri.cep_raw * 12.5), // 0-62.5
    yeraltı_suyu_azalma: Math.round(wri.gtd_raw_baseline * 17.5) // 0-87.5
  };
}

/**
 * Cache mekanizması
 */
const CACHE_KEY = 'wri_local_cache';
const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 gün

export async function fetchWRIWithCache(lat: number, lon: number) {
  const cacheKey = `${CACHE_KEY}_${lat.toFixed(2)}_${lon.toFixed(2)}`;
  
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        console.log('📦 WRI verisi cache\'ten yüklendi');
        return data;
      }
    }
  } catch (error) {
    console.warn('Cache okuma hatası:', error);
  }

  const data = await fetchWRIDataByLocation(lat, lon);
  
  if (data) {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Cache yazma hatası:', error);
    }
  }

  return data;
}
